import React, { useEffect } from 'react'
import { uniqueId } from 'lodash-es'
import { ReactSVG } from 'react-svg'
import ReactInputMask from 'react-input-mask'
import { type Control, Controller, type RegisterOptions } from 'react-hook-form'
import AirDatepicker, { type AirDatepickerOptions } from 'air-datepicker'
import { datepickerOptions } from '../composables/datepickerOptions'
import dayjs from 'dayjs'
import Field, { type IFieldProps } from './Field'
import { debounce } from 'lodash-es'

interface Props extends IFieldProps {
  name: string
  type?: string
  rules?: RegisterOptions
  autocomplete?: string
  mask?: string
  datepickerOptions?: AirDatepickerOptions<HTMLInputElement>
  control: Control<any>
  textarea?: boolean
  onChange?: (value: string) => void
  onSubmit?: (data: { query: string }) => void
  isSearch?: boolean,
  icon?: string,
  children?: React.ReactElement | string
}

function TextField(props: Props): React.ReactElement {
  const [id] = React.useState(uniqueId(props.name))
  const [passwordIsVisible, setPasswordIsVisible] = React.useState(false)
  const inputRef = React.useRef<HTMLDivElement>(null)
  const datepicker = React.useRef<any>(null)
  const [isFocused, setIsFocused] = React.useState(false)

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-expect-error
  const mask: string = props.mask

  const type = React.useMemo((): string => {
    if (props.type === 'password') {
      return passwordIsVisible ? 'text' : 'password'
    }

    if (props.type === 'date') {
      return 'text'
    }

    return props.type as string
  }, [props.type, passwordIsVisible])

  const handleFocus = (): void => {
    setIsFocused(true)
  }

  const handleBlur = (): void => {
    setIsFocused(false)
  }

  const debouncedHandleSearch = debounce((query: { query: string }) => {
    if (props.onSubmit) {
      props.onSubmit(query)
    }
  }, 300)

  const handleInputChange = (value: string) => {
    if (value.length > 0) {
      debouncedHandleSearch({ query: value })
    }
  }

  const passwordVisibilySwitcher = (): React.ReactElement => (
    <button
      type="button"
      aria-label="Switch password visibility"
      className="password-visibility-switcher"
      onClick={() => {
        setPasswordIsVisible(!passwordIsVisible)
      }}
    >
      <ReactSVG
        className={passwordIsVisible ? '' : 'hidden'}
        src="/i/svg/eye.svg"
      ></ReactSVG>
      <ReactSVG
        className={passwordIsVisible ? 'hidden' : ''}
        src="/i/svg/eye-closed.svg"
      ></ReactSVG>
    </button>
  )

  const Tag = props.textarea
    ? 'textarea'
    : props.mask
      ? ReactInputMask
      : 'input'

  return (
    <Field
      {...{
        ...props,
        label: undefined,
        className: `text-field ${props.className ?? ''} ${
          props.textarea ? '_textarea' : ''
        } ${props.isSearch ? '_isSearch' : ''} ${props.icon ? '_icon' : ''}`.trim()
      }}
      render={() => {
        return (
          <div ref={inputRef} className="text-field__input">
            {props.isSearch && (
              <ReactSVG
                className="text-field__icon"
                src="/i/svg/search.svg"
                wrapper="span"
              ></ReactSVG>
            )}
            {props.icon && (
              <ReactSVG
                className="text-field__icon"
                src={props.icon}
                wrapper="span"
              ></ReactSVG>
            )}

            <Controller
              name={props.name}
              rules={props.rules}
              control={props.control}
              render={({ field }) => {
                if (props.type === 'date') {
                  useEffect(() => {
                    if (!datepicker.current) {
                      const input = inputRef.current?.querySelector('input')

                      if (input) {
                        datepicker.current = new AirDatepicker(input, {
                          ...datepickerOptions(),
                          ...(props.datepickerOptions ?? {}),
                          onHide: () => {
                            handleBlur()
                          },
                          onSelect: ({ formattedDate }) => {
                            field.onChange(formattedDate)
                          }
                        })

                        if (field.value) {
                          const dateFormat = props.datepickerOptions?.dateFormat
                        
                          const formats = [
                            'DD.MM.YYYY',
                            'DD.M.YYYY',
                            'D.MM.YYYY',
                            ...(typeof dateFormat === 'string' ? [dateFormat.toUpperCase()] : [])
                          ]
                          const uniqueFormats  =formats.filter(function(format, index) {
                            return formats.indexOf(format) == index;
                        })
                          const dates = []
                          if (Array.isArray(field.value)) {
                            field.value.map((item) => {
                              dates.push(dayjs(item, uniqueFormats, 'ru', true))
                            })
                          } else {
                            dates.push(dayjs(field.value, uniqueFormats, 'ru', true))
                          }

                          datepicker.current.selectDate(dates, {
                            silent: true
                          })
                        }
                      }
                    }
                  }, [])
                }

                return (
                  <>
                    {props.label && !props.textarea && (
                      <label
                        htmlFor={id}
                        className={`${
                          field.value || isFocused ? '_active' : ''
                        }`}
                      >
                        {props.label}
                        {props.required && <sup>*</sup>}
                      </label>
                    )}
                    {props.type === 'date' ? (
                      <input
                        readOnly={true}
                        id={id}
                        name={props.name}
                        type={type}
                        onFocus={handleFocus}
                        onBlur={() => {
                          field.onBlur()
                          handleBlur()
                        }}
                        onChange={(e) => field.onChange(e.target.value)}
                      ></input>
                    ) : (
                      <Tag
                        placeholder={props.textarea ? props.label : undefined}
                        readOnly={props.disabled}
                        mask={mask}
                        value={field.value ?? ''}
                        autoComplete={props.autocomplete}
                        id={id}
                        name={props.name}
                        type={type}
                        onFocus={handleFocus}
                        onChange={(e) => {
                          field.onChange(e.target.value)
                          handleInputChange(e.target.value)
                        }}
                        onBlur={(e) => {
                          field.onChange(e.target.value.trim())
                          handleBlur()
                          field.onBlur()
                        }}
                      ></Tag>
                    )}
                  </>
                )
              }}
            ></Controller>
            {props.type === 'password' && passwordVisibilySwitcher()}
            {props.children}
          </div>
        )
      }}
    ></Field>
  )
}

export default TextField
